import { SearchInput } from '@cbhq/cds-web/controls';
import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TextTitle3, TextHeadline } from '@cbhq/cds-web/typography';
import { Tooltip } from '@cbhq/cds-web/overlays';

import { Icon } from '@cbhq/cds-web/icons/Icon';
import { css } from '@emotion/css';
import { useChainId } from 'wagmi';
import { PoolsListDesktopRow } from '@/components/Pools/PoolsListDesktopRow';
import { PoolsListMobileRow } from '@/components/Pools/PoolsListMobileRow';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ERROR_MESSAGE_POOLS_LIST_FAILED } from '@/app/utils/errors';
type SortDirection = 'asc' | 'desc' | null;

// Fix z-index for tooltips to appear above wallet dropdown
const TooltipStyles = css`
  z-index: 10000 !important;
`;

// Add field property to each column for sorting
const PoolsListColumns = [
  { label: 'Pools', field: 'name' },
  { label: 'TVL', tip: 'Total Value Locked in USD', field: 'tvlNotional' },
  { label: 'APR', tip: 'Annualized based on 7 day fees', field: 'apr7dPercentage' },
  { label: 'Volume 24H', tip: '24 hour trading volume in USD', field: 'volume24hNotional' },
  { label: 'Volume 7D', tip: '7 day trading volume in USD', field: 'volume7dNotional' },
  { label: 'Fees 24H', tip: '24 hour fees generated in USD', field: 'fees24hNotional' },
  { label: 'Fees 7D', tip: '7 day fees generated in USD', field: 'fees7dNotional' },
];

function PoolsListRowFallback() {
  return (
    <TableRow>
      {PoolsListColumns.map(({ label }, i) => {
        return i === 0 ? (
          <TableCell
            key={label}
            start={<Fallback width={20} height={20} shape="circle" disableRandomRectWidth />}
          >
            <HStack alignItems="center" gap={1}>
              <Fallback width={100} height={24} disableRandomRectWidth />
              <Fallback width={60} height={24} disableRandomRectWidth />
            </HStack>
          </TableCell>
        ) : (
          <TableCell key={label} alignItems="flex-end">
            <Fallback width={50} height={24} disableRandomRectWidth />
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function PoolsListItemsLoading() {
  return (
    <>
      <PoolsListRowFallback />
      <PoolsListRowFallback />
    </>
  );
}

function PoolsListItems({
  search,
  sortField,
  sortDirection,
  isMobile,
}: {
  search: string;
  sortField: string | null;
  sortDirection: SortDirection;
  isMobile: boolean;
}) {
  const chainId = useChainId();
  const {
    data: { poolsInfo },
  } = usePoolsInfo({ chainId });

  const poolsList = useMemo(() => {
    // First filter by search
    let filteredPools = poolsInfo?.filter((pool) =>
      pool.name.toLowerCase().includes(search.toLowerCase()),
    );

    // Then sort if needed
    if (sortField && sortDirection) {
      filteredPools = [...filteredPools].sort((a, b) => {
        // Handle string fields (like name) separately
        if (sortField === 'name') {
          return sortDirection === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }

        // For numeric fields
        const aValue = a[sortField as keyof typeof a] as number;
        const bValue = b[sortField as keyof typeof b] as number;

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    return filteredPools;
  }, [poolsInfo, search, sortField, sortDirection]);

  return poolsList?.map((pool) =>
    isMobile ? (
      <PoolsListMobileRow key={pool.poolId} pool={pool} />
    ) : (
      <PoolsListDesktopRow key={pool.poolId} pool={pool} />
    ),
  );
}

const PoolsListContent = memo(function PoolsListContent() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        // Cycle through: asc -> desc -> null
        const newDirection =
          sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc';
        setSortDirection(newDirection);
        if (newDirection === null) {
          setSortField(null);
        }
      } else {
        // Start with ascending for a new field
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection],
  );

  const Stack = isMobile ? VStack : HStack;

  return (
    <>
      <VStack paddingY={2} paddingX={3} borderRadius={300}>
        <Stack justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} gap={2}>
          <TextTitle3 as="h1">Explore</TextTitle3>
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search by token or pool"
            compact
            width={isMobile ? '100%' : '480px'}
          />
        </Stack>
      </VStack>
      <VStack maxWidth="100%">
        <Table>
          {!isMobile && (
            <TableHeader>
              <TableRow>
                {PoolsListColumns.map(({ label, tip, field }, i) => {
                  const isSorted = sortField === field;
                  const iconName = !isSorted
                    ? undefined
                    : sortDirection === 'asc'
                    ? 'sortUp'
                    : 'sortDown';

                  const headerContent = (
                    <HStack alignItems="center" gap={1}>
                      {iconName && <Icon name={iconName} size="xs" color="fg" />}
                      {tip ? (
                        <Tooltip content={tip} className={TooltipStyles}>
                          <TextHeadline as="span" noWrap color="fgMuted">
                            {label}
                          </TextHeadline>
                        </Tooltip>
                      ) : (
                        <TableCell title={label} as="span" noWrap />
                      )}
                    </HStack>
                  );

                  return i === 0 ? (
                    <TableCell key={label} width="20%">
                      {headerContent}
                    </TableCell>
                  ) : (
                    <TableCell
                      alignItems="flex-end"
                      onClick={() => handleSort(field)}
                      testID={`sort-column-${field}`}
                      key={label}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            <Suspense fallback={<PoolsListItemsLoading />}>
              <PoolsListItems
                search={search}
                sortField={sortField}
                sortDirection={sortDirection}
                isMobile={isMobile}
              />
            </Suspense>
          </TableBody>
        </Table>
      </VStack>
    </>
  );
});

const PoolsListWithErrorBoundary = memo(function PoolsListWithErrorBoundary() {
  return (
    <VStack background="bg" borderRadius={300}>
      <ErrorBoundary message={ERROR_MESSAGE_POOLS_LIST_FAILED}>
        <PoolsListContent />
      </ErrorBoundary>
    </VStack>
  );
});

export const PoolsList = memo(function PoolsList() {
  return <PoolsListWithErrorBoundary />;
});
